import { type FC, useCallback, useEffect, useRef, useState } from "react";

import {
  type RemoteIssueProject,
  fetchRemoteIssueProjects,
} from "../../../api/issueIntegrations";
import {
  createIssueIntegrationProjectAsUser,
  getIssueIntegrationProviderDetailAsUser,
  listIssueIntegrationProvidersAsUser,
} from "../../../dashboard-client";
import { Alert } from "../../common/Alert";
import { Button } from "../../common/Button";
import {
  ConnectUrlBlock,
  ForgeInstallCommandBlock,
} from "../../common/JiraSetupView";
import { LoadingSpinner } from "../../common/LoadingState";
import { JiraLinkCodeBlock } from "../../jira/JiraLinkCodeBlock";
import { StepSuccessCard } from "./StepSuccessCard";

type SubStep = "install" | "link-code";

interface JiraStepProps {
  isComplete: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

export const JiraStep: FC<JiraStepProps> = ({
  isComplete,
  onComplete,
  onSkip,
}) => {
  const [connectUrl, setConnectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [jiraLinked, setJiraLinked] = useState(false);
  const [subStep, setSubStep] = useState<SubStep>("install");

  const loadConnectUrl = useCallback(async () => {
    try {
      const { data } = await listIssueIntegrationProvidersAsUser();
      setConnectUrl(data.connect_url ?? null);
      setJiraLinked(
        data.providers.some((provider) => provider.type === "jira"),
      );
    } catch {
      /* Non-fatal */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadConnectUrl();
  }, [loadConnectUrl]);

  if (isComplete) {
    return (
      <StepSuccessCard
        title="Jira connected"
        description="Your Jira integration is configured."
        onContinue={onSkip}
      />
    );
  }

  if (jiraLinked) {
    return <ProjectSelectionStep onContinue={onComplete} />;
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="font-mono text-sm font-bold uppercase tracking-widest text-white">
            Connect Jira
          </h2>
          <span className="border border-slate-700 bg-arti-background px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">
            Optional
          </span>
        </div>
        <p className="mt-1 text-sm leading-6 text-slate-400">
          {subStep === "install"
            ? "Open the installation link below and complete Atlassian's install flow."
            : "Generate a link code and paste it into the Arti admin page in Jira."}
        </p>
      </div>

      {subStep === "install" && (
        <InstallSubStep
          connectUrl={connectUrl}
          loading={loading}
          onInstalled={() => setSubStep("link-code")}
          onSkip={onSkip}
        />
      )}

      {subStep === "link-code" && (
        <LinkCodeSubStep
          onLinked={() => setJiraLinked(true)}
          onBack={() => setSubStep("install")}
          onSkip={onSkip}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-step: Install the app
// ---------------------------------------------------------------------------

const InstallSubStep: FC<{
  connectUrl: string | null;
  loading: boolean;
  onInstalled: () => void;
  onSkip: () => void;
}> = ({ connectUrl, loading, onInstalled, onSkip }) => (
  <>
    {loading && (
      <div className="border border-slate-700 bg-slate-800 p-4">
        <div className="h-4 animate-pulse bg-slate-700" />
      </div>
    )}
    {!loading && connectUrl && (
      <>
        <ConnectUrlBlock connectUrl={connectUrl} />

        <Button onClick={onInstalled} fullWidth>
          I&apos;ve installed the app
        </Button>
      </>
    )}
    {!loading && !connectUrl && <ForgeInstallCommandBlock />}

    <Button onClick={onSkip} variant="ghost" fullWidth>
      Skip for now
    </Button>
  </>
);

// ---------------------------------------------------------------------------
// Sub-step: Generate & enter link code, poll for completion
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 5000;

const LinkCodeSubStep: FC<{
  onLinked: () => void;
  onBack: () => void;
  onSkip: () => void;
}> = ({ onLinked, onBack, onSkip }) => {
  const [hasCode, setHasCode] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onLinkedRef = useRef(onLinked);
  onLinkedRef.current = onLinked;

  useEffect(() => {
    if (!hasCode) {
      return;
    }

    const poll = async () => {
      try {
        const { data } = await listIssueIntegrationProvidersAsUser();
        if (data.providers.some((p) => p.type === "jira")) {
          onLinkedRef.current();
        }
      } catch {
        /* Ignore poll errors */
      }
    };

    pollRef.current = setInterval(() => void poll(), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, [hasCode]);

  return (
    <>
      <JiraLinkCodeBlock
        autoGenerate
        onCodeGenerated={() => setHasCode(true)}
      />

      {hasCode && (
        <div className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">
          <LoadingSpinner size="sm" />
          Waiting for link to complete...
        </div>
      )}

      <div className="flex gap-2">
        <Button onClick={onBack} variant="secondary" fullWidth>
          Back
        </Button>
        <Button onClick={onSkip} variant="ghost" fullWidth>
          Skip for now
        </Button>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Sub-step: Select Jira projects (shown after Jira is connected)
// ---------------------------------------------------------------------------

const ProjectSelectionStep: FC<{ onContinue: () => void }> = ({
  onContinue,
}) => {
  const [remoteProjects, setRemoteProjects] = useState<RemoteIssueProject[]>(
    [],
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [projectError, setProjectError] = useState<string | null>(null);
  const [providerId, setProviderId] = useState<string | null>(null);

  const discover = useCallback(async () => {
    setLoading(true);
    setProjectError(null);
    try {
      const { data: listData } = await listIssueIntegrationProvidersAsUser();
      const jira = listData.providers.find((p) => p.type === "jira");
      if (!jira) {
        setLoading(false);
        return;
      }
      setProviderId(jira.provider_id);

      const [{ data: detail }, remote] = await Promise.all([
        getIssueIntegrationProviderDetailAsUser({
          path: { providerId: jira.provider_id },
        }),
        fetchRemoteIssueProjects(jira.provider_id).catch(
          () => [] as RemoteIssueProject[],
        ),
      ]);

      const existingKeys = new Set(detail.projects.map((p) => p.external_key));
      const available = remote
        .filter((r) => !existingKeys.has(r.external_key))
        .toSorted((a, b) => {
          const nameA = (a.name ?? a.external_key).toLowerCase();
          const nameB = (b.name ?? b.external_key).toLowerCase();
          return nameA.localeCompare(nameB);
        });

      setRemoteProjects(available);
      setSelected(new Set(available.map((r) => r.remote_id)));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to discover projects.";
      setProjectError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void discover();
  }, [discover]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === remoteProjects.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(remoteProjects.map((r) => r.remote_id)));
    }
  };

  const handleSave = async () => {
    if (!providerId || selected.size === 0) {
      onContinue();
      return;
    }

    setSaving(true);
    setProjectError(null);
    try {
      const toAdd = remoteProjects.filter((r) => selected.has(r.remote_id));
      await Promise.all(
        toAdd.map((project) =>
          createIssueIntegrationProjectAsUser({
            body: {
              external_key: project.external_key,
              name: project.name ?? undefined,
              project_id: project.suggested_project_id || project.remote_id,
            },
            path: { providerId },
          }),
        ),
      );
      onContinue();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to add projects.";
      setProjectError(message);
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="inline-grid size-10 place-items-center border border-emerald-500/30 bg-emerald-500/10">
          <span className="font-mono text-sm text-emerald-400">■</span>
        </div>
        <div>
          <h2 className="font-mono text-sm font-bold uppercase tracking-widest text-white">
            Jira connected
          </h2>
          <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-emerald-400">
            Your Jira integration is set up.
          </p>
        </div>
      </div>

      <div>
        <h3 className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-400">
          Select projects
        </h3>
        <p className="mt-1 text-sm leading-6 text-slate-400">
          Choose which Jira projects Arti should work with. You can change this
          later in Settings.
        </p>
      </div>

      {projectError && (
        <Alert variant="warning" animate>
          {projectError}
        </Alert>
      )}

      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }, (_, i) => (
            <div
              key={i}
              className="flex items-center gap-3 border border-slate-700 bg-slate-800 px-3 py-2"
            >
              <div className="h-4 w-4 animate-pulse bg-slate-700" />
              <div
                className="h-3 flex-1 animate-pulse bg-slate-700"
                style={{ animationDelay: `${i * 100}ms` }}
              />
            </div>
          ))}
        </div>
      )}
      {!loading && remoteProjects.length > 0 && (
        <div className="space-y-1">
          <label className="flex cursor-pointer select-none items-center gap-2 border border-slate-700 bg-arti-background px-3 py-2 transition-colors hover:border-slate-600">
            <input
              type="checkbox"
              checked={selected.size === remoteProjects.length}
              onChange={toggleAll}
              className="h-4 w-4 border-slate-600 bg-arti-background text-emerald-500 focus:ring-2 focus:ring-emerald-500/30"
            />
            <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-400">
              {selected.size === remoteProjects.length
                ? "Deselect all"
                : "Select all"}
            </span>
          </label>

          <div className="max-h-64 space-y-1 overflow-y-auto">
            {remoteProjects.map((project) => (
              <label
                key={project.remote_id}
                className="flex cursor-pointer select-none items-center gap-2 border border-slate-700 bg-arti-background px-3 py-2 transition-colors hover:border-slate-600"
              >
                <input
                  type="checkbox"
                  checked={selected.has(project.remote_id)}
                  onChange={() => toggle(project.remote_id)}
                  className="h-4 w-4 border-slate-600 bg-arti-background text-emerald-500 focus:ring-2 focus:ring-emerald-500/30"
                />
                <span className="truncate font-mono text-sm text-slate-300">
                  {project.name ?? project.external_key}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
      {!loading && remoteProjects.length === 0 && (
        <div className="border border-slate-700 bg-arti-background p-4 text-center">
          <span className="font-mono text-lg text-slate-600">□</span>
          <p className="mt-1 font-mono text-[10px] text-slate-500">
            No additional projects found. You can add them later in Settings.
          </p>
        </div>
      )}

      <Button
        onClick={() => void handleSave()}
        loading={saving}
        loadingText="Adding projects..."
        fullWidth
      >
        {selected.size > 0
          ? `Add ${selected.size} ${selected.size === 1 ? "project" : "projects"} & continue`
          : "Continue"}
      </Button>

      {remoteProjects.length > 0 && (
        <Button onClick={onContinue} variant="ghost" fullWidth>
          Skip for now
        </Button>
      )}
    </div>
  );
};
